import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';
// FairwayBid: Startseiten-Abschnitte nach Login-Status und Rolle filtern
import { getAudience, filterSections, ANONYMOUS } from '../../fairwaybid/audience';
// FairwayBid: lebender Abschnitt fuer angemeldete Nutzer
import SectionDashboard from '../../fairwaybid/SectionDashboard/SectionDashboard';
// FairwayBid: Kachelreihen, die automatisch einzeilig durchlaufen
import SectionMarquee from '../../fairwaybid/SectionMarquee/SectionMarquee';
import { withMarqueeSections, MARQUEE_TYPE } from '../../fairwaybid/marquee';
// FairwayBid: Zwei-Zielgruppen-Abschnitt mit den Argumenten je Seite
import SectionBenefits from '../../fairwaybid/SectionBenefits/SectionBenefits';
import SectionSteps from '../../fairwaybid/SectionSteps/SectionSteps';
import SectionIntelligenz from '../../fairwaybid/SectionIntelligenz/SectionIntelligenz';
import {
  isBenefitSection,
  BENEFIT_TYPE,
  isStepsSection,
  STEPS_TYPE,
} from '../../fairwaybid/benefits';
import { getOwnListingsById } from '../ManageListingsPage/ManageListingsPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    currentUser,
    ownListings = [],
    offers = [],
    relatedListings = [],
  } = props;

  // FairwayBid: Bis zum Mount wird bewusst die Anonym-Ansicht gerendert, damit
  // SSR-Markup und erster Client-Render identisch sind (React-Hydration).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const assetData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // FairwayBid: Abschnitte, deren "Anchor link ID" mit public- / club- / dealer- /
  // member- beginnt, sind nur für die jeweilige Zielgruppe bestimmt.
  // Regeln stehen in src/fairwaybid/audience.js.
  const audience = hasMounted ? getAudience(currentUser) : ANONYMOUS;
  const visibleSections = filterSections(assetData?.sections, audience);

  // FairwayBid: Abschnitte mit der Anchor-ID club-* / dealer-* bekommen den
  // lebenden Dashboard-Abschnitt. Titel und Beschreibung bleiben aus der Console.
  const DASHBOARD_TYPE = 'fwb-dashboard';
  const sectionsWithDashboard = visibleSections.map(section => {
    const id = section?.sectionId || '';
    const isDashboard = id.startsWith('club-') || id.startsWith('dealer-');
    return isDashboard ? { ...section, sectionType: DASHBOARD_TYPE } : section;
  });

  // FairwayBid: Abschnitte mit vielen Kacheln (oder der Anchor-Endung
  // "-laufband") laufen als einzeiliges Laufband. Regeln: src/fairwaybid/marquee.js
  const sectionsWithMarquee = withMarqueeSections(sectionsWithDashboard);

  // FairwayBid: Der Abschnitt "Warum FairwayBid?" bekommt den gestalteten
  // Zwei-Zielgruppen-Baustein. Ueberschrift und Einleitung kommen weiter aus
  // der Console, die Argumente stehen in src/fairwaybid/benefits.js.
  const sectionsWithBenefits = sectionsWithMarquee.map(section =>
    isBenefitSection(section)
      ? { ...section, sectionType: BENEFIT_TYPE }
      : isStepsSection(section)
      ? { ...section, sectionType: STEPS_TYPE }
      : section
  );

  // FairwayBid: Der Abschnitt ueber die automatische Vorsortierung steht nur
  // Unangemeldeten gegenueber - wer eingeloggt ist, benutzt sie bereits und
  // braucht keine Erklaerung, sondern sein Dashboard.
  //
  // Er wird eingefuegt und nicht aus der Console bezogen: Die Console kennt
  // keinen Abschnittstyp mit Schwarm, und ein Abschnitt, der nur aus einer
  // Anchor-ID besteht, waere fuer den naechsten, der dort aufraeumt, ein
  // Raetsel. Eingefuegt statt ersetzt heisst auch: Es kann nichts verloren
  // gehen, was in der Console gepflegt ist.
  const KI_TYPE = 'fwb-intelligenz';

  // Er steht hinter "So funktioniert FairwayBid" und nicht direkt unter dem
  // Titelbild. Zwei Gruende:
  //
  //   Erzaehlung. Wer gerade erst gelesen hat, was die Plattform ist, will
  //   nicht als Naechstes wissen, wie sie rechnet. Erst was, dann fuer wen,
  //   dann in drei Schritten wie - und danach, als Vertiefung fuer die, die
  //   weitergelesen haben, was dabei im Hintergrund passiert.
  //
  //   Optik. Direkt unter dem Titelbild stiessen zwei grosse dunkle Flaechen
  //   ohne Uebergang aneinander. Zwischen zwei hellen Abschnitten wirkt der
  //   dunkle Block gesetzt statt angeklebt.
  //
  // Findet sich der Schritt-Abschnitt nicht, haengt er hinten an - lieber am
  // falschen Platz als gar nicht.
  const stepsIndex = sectionsWithBenefits.findIndex(sec => sec?.sectionType === STEPS_TYPE);
  const einfuegeIndex = stepsIndex >= 0 ? stepsIndex + 1 : sectionsWithBenefits.length;
  const sectionsWithKi =
    audience === ANONYMOUS && sectionsWithBenefits.length > 0
      ? [
          ...sectionsWithBenefits.slice(0, einfuegeIndex),
          { sectionId: 'fwb-intelligenz', sectionType: KI_TYPE },
          ...sectionsWithBenefits.slice(einfuegeIndex),
        ]
      : sectionsWithBenefits;

  const audienceAssetData = assetData ? { ...assetData, sections: sectionsWithKi } : assetData;

  const sectionOptions = {
    sectionComponents: {
      [DASHBOARD_TYPE]: {
        component: sectionProps => (
          <SectionDashboard
            {...sectionProps}
            audience={audience}
            ownListings={ownListings}
            offers={offers}
            relatedListings={relatedListings}
            currentUser={currentUser}
          />
        ),
      },
      [MARQUEE_TYPE]: { component: SectionMarquee },
      [BENEFIT_TYPE]: { component: SectionBenefits },
      [STEPS_TYPE]: { component: SectionSteps },
      [KI_TYPE]: { component: SectionIntelligenz },
    },
  };

  return (
    <PageBuilder
      pageAssetsData={audienceAssetData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      options={sectionOptions}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  const { currentUser } = state.user || {};
  // FairwayBid: eigene Anzeigen fuer den Dashboard-Abschnitt
  const { currentPageResultIds = [] } = state.ManageListingsPage || {};
  const ownListings = currentPageResultIds.length
    ? getOwnListingsById(state, currentPageResultIds)
    : [];
  // FairwayBid: eingegangene Angebote, geladen ueber den Posteingang-Duck
  const { transactionRefs = [] } = state.InboxPage || {};
  const offers = transactionRefs.length ? getMarketplaceEntities(state, transactionRefs) : [];
  // FairwayBid: fremde Anfragen aus dem Such-Duck. Fuer Haendler die passenden
  // aus ihren Kategorien, fuer Golfanlagen die Kandidaten fuer ein Buendel.
  const { currentPageResultIds: relatedIds = [] } = state.SearchPage || {};
  const relatedListings = relatedIds.length ? getListingsById(state, relatedIds) : [];

  return {
    pageAssetsData,
    featuredListingData,
    getListingEntitiesById,
    inProgress,
    error,
    currentUser,
    ownListings,
    offers,
    relatedListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
